import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import MediaRenderer, { resolveItemMedia } from './MediaRenderer';

const SIZE_CLASSES = {
    large: 'md:col-span-2 md:row-span-2',
    medium: 'md:col-span-2',
    small: 'md:col-span-1',
};

export default function BentoGridSection({ block = {}, primaryColor, secondaryColor, enableAnimations, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const mediaMode = config.media_mode || 'image';
    const imageFit = config.image_fit || 'cover';
    const fitClass = imageFit === 'contain' ? 'object-contain' : imageFit === 'auto' ? 'object-scale-down' : 'object-cover';
    const carouselInterval = (config.carousel_interval || 4) * 1000;
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const items = content.items || [];
    const anim = getAnimation(config, visible);

    if (items.length === 0) return null;

    const variant = config.variant || 'default';
    const bentoColumns = config.columns || 3;
    const bentoColClass = bentoColumns === 2 ? 'md:grid-cols-2' : bentoColumns === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3';

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    /* ── shared card renderer for masonry / highlight ── */
    const renderCard = (item, i, extraClass = '', isLarge = false) => {
        const resolved = resolveItemMedia(item, mediaMode, mediaFolders);
        const hasImage = !!(resolved.resolvedImage);
        return (
            <div
                key={i}
                className={`group rounded-2xl overflow-hidden transition-all duration-500 hover:shadow-xl ${extraClass} ${
                    visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: visible ? `${i * 80}ms` : '0ms' }}
            >
                <div className="w-full h-full flex flex-col" style={{ background: `linear-gradient(135deg, ${hexToRgba(s.accent, 0.06)}, ${hexToRgba(s.accent, 0.15)})` }}>
                    {hasImage && (
                        <img src={resolved.resolvedImage} alt={item.title || ''} className={`w-full ${isLarge ? 'h-48' : 'h-32'} object-cover`} loading="lazy" />
                    )}
                    <div className={isLarge ? 'p-6' : 'p-4'}>
                        {item.icon && <span className={`block ${isLarge ? 'text-4xl mb-3' : 'text-2xl mb-2'}`}>{item.icon}</span>}
                        {item.title && <h3 className={`font-bold ${isLarge ? 'text-xl' : 'text-base'}`} style={{ color: s.text }}>{item.title}</h3>}
                        {item.description && <p className={`mt-1 ${isLarge ? 'text-sm' : 'text-xs'} leading-relaxed`} style={{ color: s.textSub }}>{item.description}</p>}
                    </div>
                </div>
            </div>
        );
    };

    /* ── masonry variant ── */
    if (variant === 'masonry') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="columns-2 md:columns-3 gap-4">
                        {items.map((item, i) => (
                            <div key={i} className="break-inside-avoid mb-4">
                                {renderCard(item, i, '', false)}
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    /* ── highlight variant ── */
    if (variant === 'highlight') {
        const [first, ...rest] = items;
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {first && (
                            <div className="sm:col-span-2 sm:row-span-2">
                                {renderCard(first, 0, 'h-full', true)}
                            </div>
                        )}
                        {rest.map((item, i) => renderCard(item, i + 1, '', false))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    /* ── default variant ── */
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />

                <div className={`grid grid-cols-1 ${bentoColClass} gap-4 auto-rows-[180px] md:auto-rows-[200px]`}>
                    {items.map((item, i) => {
                        const size = item.size || 'small';
                        const resolved = resolveItemMedia(item, mediaMode, mediaFolders);
                        const hasMedia = !!(resolved.resolvedImage || resolved.carouselImages || resolved.video);
                        const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.small;

                        return (
                            <div
                                key={i}
                                className={`group relative rounded-2xl overflow-hidden transition-all duration-500 hover:shadow-xl ${sizeClass} ${
                                    visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                                }`}
                                style={{ transitionDelay: visible ? `${i * 80}ms` : '0ms' }}
                            >
                                {hasMedia ? (
                                    <>
                                        <MediaRenderer
                                            item={{ ...item, ...resolved }}
                                            fitClass={fitClass}
                                            interval={carouselInterval}
                                            accent={s.accent}
                                            aspect=""
                                            rounded=""
                                            className="w-full h-full transition-transform duration-700 group-hover:scale-110"
                                        />
                                        {/* Overlay with text */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col justify-end p-5 transition-all">
                                            {item.icon && <span className="text-2xl mb-2">{item.icon}</span>}
                                            {item.title && (
                                                <h3 className={`font-bold text-white ${size === 'large' ? 'text-xl' : 'text-base'}`}>
                                                    {item.title}
                                                </h3>
                                            )}
                                            {item.description && (
                                                <p className={`text-white/80 mt-1 transition-all duration-300 ${
                                                    size === 'small' ? 'text-xs line-clamp-2' : 'text-sm line-clamp-3'
                                                } opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0`}>
                                                    {item.description}
                                                </p>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <div
                                        className="w-full h-full flex flex-col justify-end p-5 transition-all duration-300 group-hover:shadow-inner"
                                        style={{ background: `linear-gradient(135deg, ${hexToRgba(s.accent, 0.08)}, ${hexToRgba(s.accent, 0.19)})` }}
                                    >
                                        {item.icon && <span className={`mb-3 ${size === 'large' ? 'text-5xl' : 'text-3xl'}`}>{item.icon}</span>}
                                        {item.title && (
                                            <h3 className={`font-bold ${size === 'large' ? 'text-xl' : 'text-base'}`} style={{ color: s.text }}>
                                                {item.title}
                                            </h3>
                                        )}
                                        {item.description && (
                                            <p className={`mt-1 ${size === 'small' ? 'text-xs line-clamp-2' : 'text-sm line-clamp-3'}`} style={{ color: s.textSub }}>
                                                {item.description}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
